/**
 * Worker entity DTOs.
 *
 * Mirrors Prisma `ProjectWorker` (prisma/schema.prisma:432) — the per-
 * project worker row. AccountWorker (account-level address-book) is a
 * separate concern; the optional `accountWorkerId` link is preserved so the
 * mobile UI can show "saved to address book" badges.
 *
 * WorkerRole reflects Prisma enum WorkerRole at schema.prisma:85. The
 * web-side createWorkerSchema (lib/validators.ts) currently only accepts
 * MANAGER/CONTRACTOR/STAFF — SUBCONTRACTOR is reserved for the dedicated
 * Subcontractor workflow but the enum value is exposed for completeness.
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

export const WORKER_ROLES = [
  "MANAGER",
  "CONTRACTOR",
  "SUBCONTRACTOR",
  "STAFF",
] as const;

export type WorkerRole = (typeof WORKER_ROLES)[number];

export const WorkerRoleSchema = z.enum(WORKER_ROLES);

/**
 * Certification expiry dates are entered on mobile as plain calendar dates
 * (`YYYY-MM-DD`) but may also arrive as full ISO datetimes (e.g. echoed back
 * from the server). An empty string is accepted as "not provided" so the
 * mobile text inputs can submit blank fields; the server normalises empty /
 * null to a null `DateTime` column. Non-empty values must be real calendar
 * dates; Date.parse alone would silently roll 30 February into March.
 */
const CalendarDate = z.string().date();
const IsoTimestamp = z.string().datetime({ offset: true });
export const WorkerCertificationDateInputSchema = z
  .string()
  .refine(
    (value) => value === "" || CalendarDate.safeParse(value).success || IsoTimestamp.safeParse(value).success,
    "Enter a real calendar date (YYYY-MM-DD) or an ISO datetime with a UTC offset",
  );

export const WorkerSchema = z
  .object({
    id: entityIdSchema,
    projectId: entityIdSchema,
    accountWorkerId: entityIdSchema.nullable(),
    name: z.string(),
    company: z.string().nullable(),
    role: WorkerRoleSchema,
    email: z.string().nullable(),
    phone: z.string().nullable(),
    firstAidTrained: z.boolean(),
    fireWardenTrained: z.boolean(),
    // Lightweight per-project certification tracking. Expiry fields are ISO
    // datetime strings (or null when not recorded). Mirrors the additive
    // ProjectWorker columns added in migration 20260520120000.
    cscsCardNumber: z.string().nullable(),
    cscsExpiresAt: z.string().datetime().nullable(),
    ipafExpiresAt: z.string().datetime().nullable(),
    firstAidExpiresAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
  });

export type Worker = z.infer<typeof WorkerSchema>;

// #2411 server-boundary variant: the mobile app parses the permissive base;
// the routes serialise through the Strict form.
export const WorkerStrict = WorkerSchema.strict();

/**
 * POST /api/mobile/v1/projects/:projectId/workers body. `saveToAccount`
 * mirrors the web flag — when true, also upserts the AccountWorker row so
 * the worker shows up on future projects' address-book pickers.
 */
export const WorkerCreateRequest = z
  .object({
    // Required names must remain nonblank after boundary normalization; trim
    // only the outer whitespace and preserve internal spacing as entered.
    name: z.string().trim().min(1),
    company: z.string().nullable().optional(),
    role: WorkerRoleSchema.default("STAFF"),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    firstAidTrained: z.boolean().default(false),
    fireWardenTrained: z.boolean().default(false),
    cscsCardNumber: z.string().nullable().optional(),
    cscsExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    ipafExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    firstAidExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    saveToAccount: z.boolean().default(false),
    accountWorkerId: entityIdSchema.nullable().optional(),
  })
  .strict();

export type WorkerCreateRequest = z.infer<typeof WorkerCreateRequest>;

/**
 * PATCH /api/mobile/v1/projects/:projectId/workers/:workerId body. Subset
 * of the create shape — `name` is treated as immutable (it's the unique
 * key per project).
 */
export const WorkerUpdateRequest = z
  .object({
    company: z.string().nullable().optional(),
    role: WorkerRoleSchema.optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
    firstAidTrained: z.boolean().optional(),
    fireWardenTrained: z.boolean().optional(),
    cscsCardNumber: z.string().nullable().optional(),
    cscsExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    ipafExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    firstAidExpiresAt: WorkerCertificationDateInputSchema.nullable().optional(),
    // OPTIONAL lost-update claim (#2369 route 5 / #2707): when present, the
    // edit applies only if the row's updatedAt still equals it (409
    // CONFLICT otherwise); absent = the pre-claim last-writer-wins write
    // every older build sends. Never a written field.
    expectedUpdatedAt: z.string().datetime().optional(),
  })
  .strict();

export type WorkerUpdateRequest = z.infer<typeof WorkerUpdateRequest>;

/**
 * Present only when the worker was added to a Site Access project on the
 * Advanced plan while all its sign-in places were in use (Advanced plan spec
 * v1.2 section 4.3): the worker IS added, can sign documents and see site
 * information, but cannot check in on site. `message` is the customer copy.
 */
export const WorkerSiteSignInNoticeSchema = z
  .object({ withheld: z.literal(true), message: z.string() })
  .strict();
export type WorkerSiteSignInNotice = z.infer<typeof WorkerSiteSignInNoticeSchema>;

export const WorkerResponse = z
  .object({ worker: WorkerSchema, siteSignIn: WorkerSiteSignInNoticeSchema.optional() })
  .strict();
export type WorkerResponse = z.infer<typeof WorkerResponse>;
